/**
 * Story.jsx — EVORA Our Story Page
 *
 * Fixes vs submitted version:
 *
 * 1. SITE_URL: evoraclothing.com (matches whole codebase)
 * 2. og:image added (blank social previews fixed)
 * 3. og:type: website → article (editorial content)
 * 4. JSON-LD moved to module level (no JSON.stringify on every render)
 * 5. JSON-LD upgraded: @graph with WebPage + BreadcrumbList + Organization
 *    Organization schema is the highest-value addition for a brand story page
 * 6. Self-closing <li> breadcrumb separator fixed
 * 7. Page title improved: keyword-rich, not just "Our Story — EVORA"
 * 8. H1 improved: "The EVORA Story" — has keyword value, more memorable
 * 9. max-width: 900px removed from page shell → full-width with inner containers
 * 10. Desktop: two-column layout (narrative + sticky values panel)
 * 11. Approach items: 3-column grid on desktop (matching SizeGuide FitCards)
 * 12. CTA: "Learn More About Us → /about" replaced with collection link
 *     (story page + about page competing for same intent = cannibalisation)
 * 13. Inline collection nudge added after Approach section (mid-funnel capture)
 * 14. "Why Now?" section humanised — macro trend paragraphs → concrete values
 * 15. contain: layout style → contain: layout (style containment can break
 *     CSS custom property inheritance from parent scope)
 * 16. IconCheck import removed (was imported but never used)
 */

import { Link }   from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout     from '../../components/common/Layout/Layout';
import { BRAND }  from '../../config/brand';
import './Story.css';

// ── Constants ──────────────────────────────────────────────────────────────
// CORRECTED: evoraclothing.com — consistent with all other pages in the codebase
const SITE_URL   = process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const OG_IMAGE   = `${SITE_URL}/og-image.jpg`;
const BRAND_YEAR = 2026;

// ── Approach items — module scope, never recreated ─────────────────────────
const APPROACH_ITEMS = [
  {
    id: 'small',
    num: '01',
    title: 'Start Small, Think Big',
    text: 'We launched with a focused collection of premium essentials. By starting small we ensure every piece meets our exacting standards. As we grow, we expand thoughtfully — never at the expense of quality.',
  },
  {
    id: 'evolve',
    num: '02',
    title: 'Learn and Evolve',
    text: `As a new brand we're constantly learning from our customers. Your feedback shapes everything — product design, sizing, customer service. We're building ${BRAND.name} with you, not just for you.`,
  },
  {
    id: 'future',
    num: '03',
    title: 'Build for the Future',
    text: "We're not just making products — we're building a brand that will stand the test of time. Every decision considers the long-term impact on our customers, our partners, and our planet.",
  },
];

// Values shown in the desktop sticky sidebar
const VALUES = [
  { id: 'quality',       label: 'Uncompromising quality' },
  { id: 'transparency',  label: 'Full supply chain transparency' },
  { id: 'craftsmanship', label: 'Expert craftsmanship' },
  { id: 'timeless',      label: 'Timeless, not trendy' },
  { id: 'responsibility',label: 'Responsible manufacturing' },
  { id: 'community',     label: 'Community-first brand' },
];

// ── Structured data — serialized once at module level ─────────────────────
const PAGE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type':       'WebPage',
      '@id':         `${SITE_URL}/story#webpage`,
      url:           `${SITE_URL}/story`,
      name:          `About EVORA | Premium T-Shirts, Built to Last | Founded ${BRAND_YEAR}`,
      description:   `Discover the story behind EVORA — a premium fashion brand founded in ${BRAND_YEAR} to create timeless, quality essentials built to last.`,
      inLanguage:    'en-IN',
      isPartOf:      { '@id': `${SITE_URL}/#website` },
      breadcrumb:    { '@id': `${SITE_URL}/story#breadcrumb` },
      /*
        og:type is article — editorial content.
        datePublished helps Google understand content freshness.
      */
      datePublished: `${BRAND_YEAR}-01-01`,
    },
    {
      '@type': 'BreadcrumbList',
      '@id':   `${SITE_URL}/story#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',      item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Our Story', item: `${SITE_URL}/story` },
      ],
    },
    /*
      Organization schema — highest-value addition for a brand story page.
      Tells Google's Knowledge Graph who EVORA is, when it was founded,
      and what it sells. Enables brand knowledge panel in search results.
    */
    {
      '@type':        'Organization',
      '@id':          `${SITE_URL}/#organization`,
      name:           BRAND.name,
      url:            SITE_URL,
      logo:           `${SITE_URL}/logo.png`,
      foundingDate:   String(BRAND_YEAR),
      description:    `${BRAND.name} is a premium Indian fashion brand specialising in timeless, high-quality t-shirts for men and women. Founded in ${BRAND_YEAR}.`,
      sameAs:         [BRAND.instagram].filter(Boolean),
      contactPoint: {
        '@type':            'ContactPoint',
        contactType:        'customer support',
        availableLanguage:  ['English', 'Hindi'],
      },
    },
  ],
});

// ── Inline SVG icons — zero bundle dependency ─────────────────────────────
function IconArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

// ── Component ──────────────────────────────────────────────────────────────
export default function Story() {
  const pageTitle = `About EVORA | Premium T-Shirts, Built to Last | Founded ${BRAND_YEAR}`;
  const pageDesc  = `Discover the story behind ${BRAND.name} — a premium Indian fashion brand founded in ${BRAND_YEAR} to create timeless, quality essentials that outlast trends and fast fashion.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description"         content={pageDesc} />
        <link rel="canonical"            href={`${SITE_URL}/story`} />
        <meta name="robots"              content="index, follow" />

        {/* Open Graph — article type for editorial content */}
        <meta property="og:type"              content="article" />
        <meta property="og:site_name"         content={BRAND.name} />
        <meta property="og:title"             content={pageTitle} />
        <meta property="og:description"       content={pageDesc} />
        <meta property="og:url"               content={`${SITE_URL}/story`} />
        <meta property="og:image"             content={OG_IMAGE} />
        <meta property="og:image:width"       content="1200" />
        <meta property="og:image:height"      content="630" />
        <meta property="og:locale"            content="en_IN" />
        <meta property="article:published_time" content={`${BRAND_YEAR}-01-01T00:00:00+05:30`} />

        {/* Twitter */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image"       content={OG_IMAGE} />

        <script type="application/ld+json">{PAGE_SCHEMA}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="story-page" id="main-content">

          {/* ── Full-width hero ────────────────────────────────────────── */}
          <div className="story-hero">
            <div className="story-hero__inner">

              {/* Breadcrumb */}
              <nav className="story-breadcrumb" aria-label="Breadcrumb">
                <ol className="story-breadcrumb__list">
                  <li>
                    <Link to="/" className="story-breadcrumb__link">Home</Link>
                  </li>
                  {/* Valid HTML: non-void <li> with content */}
                  <li aria-hidden="true" className="story-breadcrumb__sep">
                    <span>›</span>
                  </li>
                  <li>
                    <span aria-current="page" className="story-breadcrumb__current">
                      Our Story
                    </span>
                  </li>
                </ol>
              </nav>

              <p className="story-hero__eyebrow" aria-hidden="true">Founded {BRAND_YEAR}</p>
              {/*
                H1 improved: includes brand name + keyword hook.
                "Our Story" as H1 has near-zero search value.
              */}
              <h1 className="story-hero__title">
                The {BRAND.name} Story
              </h1>
              <p className="story-hero__subtitle">
                Born from a belief that fashion can be both beautiful and responsible —
                premium essentials, made to last, designed with intention.
              </p>

            </div>
          </div>

          {/* ── Two-column body layout ─────────────────────────────────── */}
          <div className="story-layout">

            {/* ── Sticky values sidebar (desktop only) ─────────────────── */}
            <aside className="story-sidebar" aria-label="Brand values">
              <div className="story-sidebar__inner">
                <p className="story-sidebar__label">What we stand for</p>
                <ul className="story-sidebar__values">
                  {VALUES.map(({ id, label }) => (
                    <li key={id} className="story-sidebar__value">
                      <span className="story-sidebar__value-icon" aria-hidden="true">
                        <IconCheck />
                      </span>
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
                <div className="story-sidebar__divider" />
                <Link to="/shop" className="story-sidebar__cta">
                  Shop →
                </Link>
              </div>
            </aside>

            {/* ── Narrative content ─────────────────────────────────────── */}
            <div className="story-main">

              {/* ── How It All Started ─────────────────────────────────── */}
              <section className="story-section" aria-labelledby="beginning-h">
                <h2 id="beginning-h" className="story-section__title">
                  How It All Started
                </h2>
                <div className="story-section__content">
                  <p className="story-section__paragraph">
                    {BRAND.name} was founded in {BRAND_YEAR} with one clear belief: that premium fashion
                    doesn't have to mean excess. We saw a gap — a need for timeless pieces made to last,
                    crafted with care, priced fairly.
                  </p>
                  <p className="story-section__paragraph">
                    As a new brand, we had the freedom to do things differently. Not bound by old supply
                    chains or legacy thinking, we built {BRAND.name} from the ground up with quality,
                    transparency, and the customer at the centre of every decision.
                  </p>
                  <p className="story-section__paragraph">
                    It started with a simple question: <em>"What if fashion could be better?"</em> Better
                    for you, better for the people who make it, better for how long it lasts. That question
                    became our mission. {BRAND_YEAR} is where it begins.
                  </p>
                </div>
              </section>

              {/* ── What Inspires Us ───────────────────────────────────── */}
              <section className="story-section story-section--highlight" aria-labelledby="inspiration-h">
                <h2 id="inspiration-h" className="story-section__title">
                  What Inspires Us
                </h2>
                <div className="story-section__content">
                  <p className="story-section__paragraph">
                    We're inspired by the idea of timeless elegance — pieces that never go out of style
                    because they were never meant to follow trends. The kind of wardrobe foundation you
                    reach for every day, for years.
                  </p>
                  <p className="story-section__paragraph">
                    Our design philosophy is simple: <strong>less is more.</strong> We perfect the
                    essentials. Premium t-shirts, thoughtfully designed, impeccably constructed. Versatile,
                    comfortable, enduring.
                  </p>
                  <p className="story-section__paragraph">
                    We're also inspired by customers who are done with disposable fashion. People who want
                    quality, transparency, and brands that mean what they say. That's exactly who we're
                    building this for.
                  </p>
                </div>
              </section>

              {/* ── Our Approach ───────────────────────────────────────── */}
              <section className="story-section" aria-labelledby="approach-h">
                <h2 id="approach-h" className="story-section__title">Our Approach</h2>
                <div className="story-approach">
                  {APPROACH_ITEMS.map(({ id, num, title, text }) => (
                    <div key={id} className="story-approach__item">
                      <div className="story-approach__number" aria-hidden="true">{num}</div>
                      <div className="story-approach__content">
                        <h3 className="story-approach__title">{title}</h3>
                        <p  className="story-approach__text">{text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/*
                Mid-funnel nudge — after reading the approach,
                readers have highest purchase intent.
                Previously there was no CTA until the very bottom.
              */}
              <div className="story-nudge">
                <p className="story-nudge__text">
                  See what we've built so far →
                </p>
                <Link to="/shop" className="story-nudge__link">
                  Shop
                  <IconArrowRight />
                </Link>
              </div>

              {/* ── Looking Ahead ──────────────────────────────────────── */}
              <section className="story-section" aria-labelledby="future-h">
                <h2 id="future-h" className="story-section__title">Looking Ahead</h2>
                <div className="story-section__content">
                  <p className="story-section__paragraph">
                    {BRAND_YEAR} is just the beginning. Our focus right now is on perfecting what we have —
                    creating the best possible products and exceptional customer experiences.
                  </p>
                  <p className="story-section__paragraph">
                    In the coming years you'll see us expand our collection thoughtfully, always maintaining
                    our commitment to quality and sustainability. New materials, new techniques, a smaller
                    environmental footprint.
                  </p>
                  <p className="story-section__paragraph">
                    But one thing will never change: our dedication to creating premium fashion you can feel
                    good about wearing. We're in this for the long haul.
                  </p>
                </div>
              </section>

              {/* ── What We Believe ────────────────────────────────────── */}
              {/*
                Renamed from "Why Now?" — macro industry trends don't convert.
                Concrete brand values and a human voice do.
              */}
              <section className="story-section story-section--highlight" aria-labelledby="values-h">
                <h2 id="values-h" className="story-section__title">What We Believe</h2>
                <div className="story-section__content">
                  <p className="story-section__paragraph">
                    We believe you shouldn't have to choose between quality and affordability.
                    Between style and sustainability. Between a brand that looks good and one that does good.
                  </p>
                  <p className="story-section__paragraph">
                    We believe the clothes you wear every day deserve the same care as the clothes you wear
                    once a year. That a well-made t-shirt is not a basic — it's the foundation of everything.
                  </p>
                  <p className="story-section__paragraph">
                    We believe that if you're going to build a brand in {BRAND_YEAR}, it should be one
                    worth building. One that proves the fashion industry can be better.{' '}
                    <strong>That's {BRAND.name}.</strong>
                  </p>
                </div>
              </section>

              {/* ── CTA ────────────────────────────────────────────────── */}
              <section className="story-cta" aria-labelledby="cta-h">
                <h2 id="cta-h" className="story-cta__title">Be Part of Our Story</h2>
                <p className="story-cta__text">
                  As we write the next chapters of {BRAND.name}, we'd love to have you along.
                </p>
                <div className="story-cta__actions">
                  {/*
                    Primary CTA: collection (direct conversion intent).
                    Previous: "Learn More → /about" (another editorial page,
                    no conversion value, competes with /story for brand searches).
                  */}
                  <Link to="/shop" className="story-cta__btn story-cta__btn--primary">
                    Shop
                    <IconArrowRight />
                  </Link>
                  <Link to="/contact" className="story-cta__btn story-cta__btn--ghost">
                    Get in Touch
                    <IconArrowRight />
                  </Link>
                </div>
              </section>

            </div>{/* /story-main */}
          </div>{/* /story-layout */}

        </main>
      </Layout>
    </>
  );
}